import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, MenuItem, Select,
  FormControl, InputLabel, IconButton, Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import firebase from '../utilities/firebase';
import { StateManager } from '../utilities/stateManager';
import { useStackZIndex, ModalStackProvider, useSelectMenuProps } from '../utilities/ModalStackContext';
import moment from 'moment';

export default function EditDepositModal({ open, onClose, deposit, onSaved }) {
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open && deposit) {
      setForm({
        account: deposit.account || '',
        amount: deposit.amount != null ? String(deposit.amount) : '',
        memo: deposit.memo || '',
        date: deposit.date
          ? moment(deposit.date, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD']).format('YYYY-MM-DD')
          : '',
        stock: deposit.stock || '',
      });
    }
  }, [open, deposit]);

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.account || !form.amount || !form.memo) {
      StateManager.setAlertAndOpen('Please fill in all required fields', 'error');
      return;
    }
    setSaving(true);
    try {
      const updates = {
        account: form.account,
        amount: parseFloat(form.amount) || 0,
        memo: form.memo,
        date: form.date ? moment(form.date).format('YYYY/MM/DD') : (deposit.date || ''),
        stock: form.stock || deposit.stock,
      };
      await firebase.firestore().collection('deposits').doc(deposit.id).update(updates);
      StateManager.setAlertAndOpen('Deposit updated', 'success');
      if (onSaved) onSaved({ ...deposit, ...updates });
      onClose();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Failed to update deposit', 'error');
    }
    setSaving(false);
  };

  if (!deposit) return null;

  return (
    <ModalStackProvider value={myZ}>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: myZ }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Deposit</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Receiving Bank</InputLabel>
              <Select value={form.account || ''} onChange={handleChange('account')} label="Receiving Bank" MenuProps={selectMenuProps}>
                {StateManager.deposit_banks.map(bank => (
                  <MenuItem key={bank.value} value={bank.value}>{bank.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" label="Amount" type="number"
              value={form.amount || ''} onChange={handleChange('amount')} required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" label="Date" type="date"
              value={form.date || ''} onChange={handleChange('date')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Memo"
              value={form.memo || ''} onChange={handleChange('memo')} required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Stock #"
              value={form.stock || ''} onChange={handleChange('stock')}
              helperText="Reassign deposit to a different car"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
}
